import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { NODES, TERRAIN, BIOMES } from '../config';
import { terrainHeight, terrainSlope } from '../engine/terrain';
import { biomeAt, forestDensity, Biome } from '../engine/biome';
import { applyInteriorDark } from '../engine/interior';
import { applyCloudShadow } from '../engine/clouds';
import { creasedNormals } from '../engine/creased';
import { applyStoneDetail } from '../engine/stonedetail';
import type { Box, BoxPool } from '../engine/collision';

// Placed ROCK FORMATIONS: the placed-mesh half of "rocks like Rust", complementing the baked terrain
// outcrops. Three low-poly archetypes (boulder cluster, tall spire, flat walkable shelf) built once from the
// same lumpy boulder geometry the ore nodes use, scattered in CLUSTERS across the map (biome-aware: more on
// rocky/desert/steep/high ground, fewer in dense forest/beach), seated on the terrain. They STREAM exactly
// like the trees + bushes (only the ones within NODES.drawRadius upload/draw, re-streamed on a stream-cell
// cross) so they are free at distance, and they feed oriented COLLISION boxes for the nearby rocks from a
// 48m grid -- decoupled from rendering, so a rock is solid within reach even when its mesh is culled far off.
// Non-harvestable. The shelf archetype's collider top == its visual top, so you can stand/build on it.

const R_CELL = 48, R_OFF = 4096, R_STRIDE = 8192; // collision + stream grid key packing (matches nodes/bushes)
const UP = new THREE.Vector3(0, 1, 0);
const ROCK_SEED = 20260623;
const SPAWN_CLEAR = 26; // no rocks within this radius of origin (spawnByWater searches outward from there)

// Per-biome rock look: a colour tint (multiplied onto the boulder's baked grey gradient via instanceColor) and a
// size lean, so desert rocks read brown, forest/grassland rocks read mossy, mountains big + cool-grey, etc.
function rgbLin(h: number): [number, number, number] {
  const s = (c: number): number => { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  return [s((h >> 16) & 255), s((h >> 8) & 255), s(h & 255)];
}
// Indexed by Biome: Ocean,Beach,Grassland,Forest,Meadow,Desert,Rocky,Hills,Mountain,Snow,Irradiated
const ROCK_TINT = [0x8a8f96, 0xb3a684, 0x7e8668, 0x6e7a56, 0x828c6c, 0xa67c50, 0x8c8f95, 0x808a6c, 0x9296a0, 0xb8bec6, 0x585b40].map(rgbLin);
const ROCK_SIZE = [1.0, 1.05, 0.92, 0.88, 0.92, 1.18, 1.25, 1.0, 1.32, 1.12, 1.05]; // size lean per biome

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// A rounded, lumpy unit boulder (same recipe as the ore nodes): squashed wider-than-tall, with a baked
// dark-base -> bright-crown vertex gradient. Archetypes scale/translate/merge copies of this.
function makeBoulder(): THREE.BufferGeometry {
  const geo = new THREE.IcosahedronGeometry(1, 2);
  const pos = geo.attributes.position;
  const v = new THREE.Vector3();
  let minY = Infinity, maxY = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i).normalize();
    const l = 0.16 * Math.sin(v.x * 3.1 + 1.0) * Math.sin(v.y * 2.7) + 0.10 * Math.sin(v.z * 4.2 + 2.0) + 0.07 * Math.cos(v.x * 5.5 + v.z * 3.3);
    v.multiplyScalar(1 + l);
    v.y = v.y * 0.82 - 0.05;
    pos.setXYZ(i, v.x, v.y, v.z);
    if (v.y < minY) minY = v.y;
    if (v.y > maxY) maxY = v.y;
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  const colors = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = (pos.getY(i) - minY) / (maxY - minY);
    const c = 0.4 + 0.6 * t;
    colors[i * 3] = c; colors[i * 3 + 1] = c; colors[i * 3 + 2] = c;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return geo;
}

function lump(sx: number, sy: number, sz: number, tx: number, ty: number, tz: number, rotY: number): THREE.BufferGeometry {
  const g = makeBoulder();
  g.rotateY(rotY);
  g.scale(sx, sy, sz);
  g.translate(tx, ty, tz);
  return g;
}

interface Arch { geom: THREE.BufferGeometry; hx: number; hy: number; hz: number; cy: number; }

// Merge the lumps, seat the base at y=0, and derive a collision box. Obstacle archetypes get a box UNDER the
// silhouette (so the player slides off, never trapped under an overhang); the shelf's box top == its visual
// top so the player stands on it (resolvePlayer lands on box tops).
function finalize(parts: THREE.BufferGeometry[], kind: 'cluster' | 'spire' | 'shelf'): Arch {
  let merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  if (!merged) throw new Error('rock archetype merge failed: ' + kind);
  merged.computeBoundingBox();
  merged.translate(0, -merged.boundingBox!.min.y, 0); // base on the ground (min.y = 0)
  merged.computeBoundingBox();
  // CREASED normals (MODELING.md): sharp joins keep hard edges, flat runs stay smooth -> chipped
  // stone facets instead of the smooth-blob lattice look. Vertex colours survive (non-indexed in/out).
  merged = creasedNormals(merged, 30);
  merged.computeBoundingBox(); // creased may hand back a fresh (non-indexed) geometry without bounds
  const b = merged.boundingBox!;
  const hx = (b.max.x - b.min.x) / 2, hz = (b.max.z - b.min.z) / 2, top = b.max.y;
  if (kind === 'shelf') return { geom: merged, hx: hx * 0.92, hy: top / 2, hz: hz * 0.92, cy: top / 2 };
  if (kind === 'spire') return { geom: merged, hx: hx * 0.55, hy: top / 2, hz: hz * 0.55, cy: top / 2 };
  return { geom: merged, hx: hx * 0.74, hy: top * 0.45, hz: hz * 0.74, cy: top * 0.45 }; // cluster: low, under the silhouette
}

// A rounded boulder pile (~2.6m wide, ~1.9m tall): obstacle, no walkable top.
function buildCluster(): Arch {
  return finalize([
    lump(1.4, 1.0, 1.3, 0, 0, 0, 0.3),
    lump(0.95, 0.85, 1.0, 0.95, 0, 0.45, 1.1),
    lump(0.85, 0.7, 0.9, -0.8, 0, -0.55, 2.0),
    lump(0.6, 0.6, 0.6, 0.25, 0.35, -0.95, 0.7),
  ], 'cluster');
}
// A leaning stack ~4m tall, thin: pure obstacle / landmark.
function buildSpire(): Arch {
  return finalize([
    lump(0.75, 1.3, 0.75, 0, 0, 0, 0.2),
    lump(0.55, 1.1, 0.55, 0.12, 1.7, 0.05, 1.7),
    lump(0.36, 0.8, 0.36, 0.22, 3.0, 0.1, 3.1),
  ], 'spire');
}
// A wide flat mesa ~5m x 4m, ~1.1m tall: WALKABLE -- the collider top is its visual top, so you stand/build on it.
function buildShelf(): Arch {
  return finalize([
    lump(2.4, 0.55, 2.0, 0, 0, 0, 0.4),
    lump(0.95, 0.42, 0.95, 1.4, 0.0, 0.55, 1.3),
  ], 'shelf');
}
// A single big rounded boulder (~3m) with one shoulder lump: a solid landmark obstacle.
function buildBoulder(): Arch {
  return finalize([
    lump(1.7, 1.5, 1.6, 0, 0, 0, 0.5),
    lump(0.75, 0.6, 0.7, 0.85, 0.05, -0.5, 1.3),
  ], 'cluster');
}
// A low rubble scatter of small boulders spread over ~4m: a broken-ground field, low obstacle.
function buildRubble(): Arch {
  return finalize([
    lump(0.75, 0.5, 0.75, -1.3, 0, 0.4, 0.4),
    lump(0.55, 0.4, 0.6, 0.7, 0, -1.0, 1.4),
    lump(0.9, 0.55, 0.85, 1.35, 0, 0.75, 2.3),
    lump(0.45, 0.32, 0.45, -0.3, 0, -0.25, 0.9),
    lump(0.6, 0.42, 0.55, 0.15, 0, 1.35, 1.8),
  ], 'cluster');
}

const ARCHETYPES: Arch[] = [buildCluster(), buildSpire(), buildShelf(), buildBoulder(), buildRubble()];

export class RockField {
  readonly group = new THREE.Group();
  private readonly meshes: THREE.InstancedMesh[] = []; // one per archetype
  private readonly visibleByArch: number[][] = [];     // rock indices currently uploaded per archetype mesh
  private readonly grid = new Map<number, number[]>(); // rock indices by 48m cell (collision + stream both read this)
  private readonly MAX = NODES.rockClusters * NODES.rockClusterMax;
  private readonly rx = new Float32Array(this.MAX);
  private readonly rz = new Float32Array(this.MAX);
  private readonly ry = new Float32Array(this.MAX);
  private readonly ryaw = new Float32Array(this.MAX);
  private readonly rscale = new Float32Array(this.MAX);
  private readonly rarch = new Uint8Array(this.MAX);
  private readonly rtint = new Float32Array(this.MAX * 3); // per-rock biome colour tint (linear RGB)
  private readonly tmpColor = new THREE.Color();
  private count = 0;
  private streamCellX = NaN; private streamCellZ = NaN;
  private rng = mulberry32(ROCK_SEED);
  private readonly tmp = new THREE.Matrix4();
  private readonly tmpPos = new THREE.Vector3();
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly tmpScale = new THREE.Vector3();

  constructor() {
    const mat = new THREE.MeshStandardMaterial({
      color: 0xffffff, vertexColors: true, roughness: 0.95, metalness: 0, // white base; the per-biome tint rides on instanceColor
    });
    applyStoneDetail(mat, 'rockformation'); // strata bands + mineral speckle + crevice AO (world-space, never tiles)
    applyInteriorDark(mat, true); // darken/reveal with the base interior cone, like ore + ground
    applyCloudShadow(mat, 'rock'); // drifting cloud shadows on the rock formations too
    for (let a = 0; a < ARCHETYPES.length; a++) {
      const mesh = new THREE.InstancedMesh(ARCHETYPES[a].geom, mat, NODES.rockCap);
      mesh.name = 'rockformation';
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.count = 0;
      mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(NODES.rockCap * 3), 3); // per-rock biome tint
      this.meshes.push(mesh);
      this.visibleByArch.push([]);
      this.group.add(mesh);
    }
    this.fill();
  }

  regenerate(): void { this.fill(); }

  // Cluster scatter (like Rust rock fields): pick cluster CENTRES biased to rocky/steep/high ground, then drop
  // a few rocks tightly around each. Deterministic. Run once at boot / on regenerate; no per-frame cost.
  private fill(): void {
    this.rng = mulberry32(ROCK_SEED);
    this.grid.clear();
    this.count = 0;
    for (let c = 0; c < NODES.rockClusters && this.count < this.MAX; c++) {
      const centre = this.scatterClusterCentre();
      if (!centre) continue;
      const [cxw, czw] = centre;
      const per = NODES.rockClusterMin + ((this.rng() * (NODES.rockClusterMax - NODES.rockClusterMin + 1)) | 0);
      for (let j = 0; j < per && this.count < this.MAX; j++) {
        let px = cxw, pz = czw, ok = false;
        for (let t = 0; t < 10; t++) {
          const a = this.rng() * Math.PI * 2, r = this.rng() * NODES.rockClusterSpread;
          px = cxw + Math.cos(a) * r; pz = czw + Math.sin(a) * r;
          const h = terrainHeight(px, pz);
          if (h <= TERRAIN.seaLevel + TERRAIN.beachWidth) continue; // off ocean + beach sand
          if (px * px + pz * pz < SPAWN_CLEAR * SPAWN_CLEAR) continue; // never on the spawn point
          ok = true; break;
        }
        if (!ok) continue;
        const i = this.count++;
        const h0 = terrainHeight(px, pz);
        const biome = biomeAt(px, pz, h0); // per-zone look: tint + size lean
        this.rx[i] = px; this.rz[i] = pz;
        this.ryaw[i] = this.rng() * Math.PI * 2;
        this.rscale[i] = (NODES.rockScaleMin + this.rng() * (NODES.rockScaleMax - NODES.rockScaleMin)) * ROCK_SIZE[biome];
        this.rarch[i] = this.pickArch(px, pz);
        // SEAT INTO the ground, never float: take the LOWEST terrain under the footprint (slopes drop the
        // downhill edge) and sink a little extra, so every rock is embedded no matter the grade.
        const A = ARCHETYPES[this.rarch[i]], s = this.rscale[i];
        const fr = Math.max(A.hx, A.hz) * s * 0.8;
        let ry = h0;
        for (const [ox, oz] of [[fr, 0], [-fr, 0], [0, fr], [0, -fr]] as const) ry = Math.min(ry, terrainHeight(px + ox, pz + oz));
        this.ry[i] = ry - 0.14 * s;
        const t = ROCK_TINT[biome]; this.rtint[i * 3] = t[0]; this.rtint[i * 3 + 1] = t[1]; this.rtint[i * 3 + 2] = t[2];
        const key = (Math.floor(px / R_CELL) + R_OFF) * R_STRIDE + (Math.floor(pz / R_CELL) + R_OFF);
        let b = this.grid.get(key); if (!b) { b = []; this.grid.set(key, b); }
        b.push(i);
      }
    }
    for (let a = 0; a < this.meshes.length; a++) { this.meshes[a].count = 0; this.visibleByArch[a].length = 0; }
    this.streamCellX = NaN; this.streamCellZ = NaN; // force a re-stream
  }

  private scatterClusterCentre(): [number, number] | null {
    for (let t = 0; t < 30; t++) {
      const a = this.rng() * Math.PI * 2;
      const d = NODES.rockScatterInner + this.rng() * (NODES.scatterRadius - NODES.rockScatterInner);
      const x = Math.cos(a) * d, z = Math.sin(a) * d;
      const h = terrainHeight(x, z);
      if (h <= TERRAIN.seaLevel + TERRAIN.beachWidth) continue;
      const bm = biomeAt(x, z, h);
      const sb = BIOMES[bm].stoneBias;                           // 0..2 (rocky/mountain high)
      const sl = Math.min(1, terrainSlope(x, z, 1.2) / 0.9);     // steeper ground -> likelier
      const fp = 1 - 0.25 * forestDensity(x, z);                 // only slightly fewer in dense forest now
      // High floor (0.34) so rocks appear in EVERY biome; stoneBias/slope still bias toward rocky/steep ground.
      let accept = Math.min(1, (0.34 + 0.33 * (sb / 2) + 0.33 * sl) * fp);
      if (bm === Biome.Desert) accept = Math.max(accept, 0.95); // DESERTS are rocky badlands: dense boulder/mesa cover so bases aren't out in the open
      if (this.rng() < accept) return [x, z];
    }
    return null;
  }

  private pickArch(x: number, z: number): number {
    const sl = terrainSlope(x, z, 1.2), r = this.rng();
    if (sl > 0.7) return r < 0.5 ? 1 : 3;  // steep ground: a spire or a big boulder
    if (sl < 0.35 && r < 0.16) return 2;   // flat ground: walkable shelf/mesa (rarer)
    if (r < 0.34) return 3;                // big single boulder
    if (r < 0.60) return 4;                // low rubble field
    return 0;                              // boulder cluster (default)
  }

  // Stream: re-upload only the rocks within NODES.drawRadius into compacted per-archetype slots.
  private streamRebuild(camX: number, camZ: number): void {
    const R = NODES.drawRadius, r2 = R * R;
    for (let a = 0; a < this.meshes.length; a++) this.visibleByArch[a].length = 0;
    const cx0 = Math.floor((camX - R) / R_CELL), cx1 = Math.floor((camX + R) / R_CELL);
    const cz0 = Math.floor((camZ - R) / R_CELL), cz1 = Math.floor((camZ + R) / R_CELL);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const bucket = this.grid.get((cx + R_OFF) * R_STRIDE + (cz + R_OFF));
        if (!bucket) continue;
        for (let bi = 0; bi < bucket.length; bi++) {
          const i = bucket[bi];
          const dx = this.rx[i] - camX, dz = this.rz[i] - camZ;
          if (dx * dx + dz * dz > r2) continue;
          const a = this.rarch[i], vis = this.visibleByArch[a], slot = vis.length;
          if (slot >= NODES.rockCap) continue;
          this.tmpPos.set(this.rx[i], this.ry[i], this.rz[i]);
          this.tmpQuat.setFromAxisAngle(UP, this.ryaw[i]);
          const s = this.rscale[i]; this.tmpScale.set(s, s, s);
          this.tmp.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
          this.meshes[a].setMatrixAt(slot, this.tmp);
          this.meshes[a].setColorAt(slot, this.tmpColor.setRGB(this.rtint[i * 3], this.rtint[i * 3 + 1], this.rtint[i * 3 + 2]));
          vis.push(i);
        }
      }
    }
    for (let a = 0; a < this.meshes.length; a++) {
      const m = this.meshes[a];
      m.count = this.visibleByArch[a].length;
      m.instanceMatrix.needsUpdate = true;
      if (m.instanceColor) m.instanceColor.needsUpdate = true;
      m.computeBoundingSphere();
    }
  }

  update(camX: number, camZ: number): void {
    const cellX = Math.floor(camX / NODES.streamCell), cellZ = Math.floor(camZ / NODES.streamCell);
    if (cellX !== this.streamCellX || cellZ !== this.streamCellZ) {
      this.streamCellX = cellX; this.streamCellZ = cellZ;
      this.streamRebuild(camX, camZ);
    }
  }

  // Oriented collision boxes for rocks within `range` of (px,pz), read from the GRID (not the draw stream) so
  // a rock blocks the player even when its mesh is culled at distance. The shelf's box top is its visual top.
  collectColliders(out: Box[], pool: BoxPool, px: number, pz: number, range: number): void {
    const r2 = range * range;
    const cx0 = Math.floor((px - range) / R_CELL), cx1 = Math.floor((px + range) / R_CELL);
    const cz0 = Math.floor((pz - range) / R_CELL), cz1 = Math.floor((pz + range) / R_CELL);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const bucket = this.grid.get((cx + R_OFF) * R_STRIDE + (cz + R_OFF));
        if (!bucket) continue;
        for (let bi = 0; bi < bucket.length; bi++) {
          const i = bucket[bi];
          const dx = this.rx[i] - px, dz = this.rz[i] - pz;
          if (dx * dx + dz * dz > r2) continue;
          const A = ARCHETYPES[this.rarch[i]], s = this.rscale[i];
          out.push(pool.take(this.rx[i], this.ry[i] + A.cy * s, this.rz[i], A.hx * s, A.hy * s, A.hz * s, this.ryaw[i]));
        }
      }
    }
  }
}
